import { calculateRoi } from 'src/utils/calculateRoi';
import { sortEpoch } from 'src/utils/date';
import { MMDataState, TimeframeData } from '../types';

const calculateRoiFromRecords = (
  byXInvestments: Record<string, number>,
  byXDecomps: Record<string, number>,
  isEuro: boolean,
): Record<string, number> => {
  const resultRecord: Record<string, number> = {};

  Object.entries(byXInvestments).forEach(([key, investmentValue]) => {
    const decompsValue = byXDecomps[key];

    if (decompsValue === undefined) {
      return;
    }

    resultRecord[key] = calculateRoi({
      mediaIncrementalSales: decompsValue,
      investment: investmentValue,
      isEuro,
    });
  });

  return resultRecord;
};

const combineTimeframeData = (
  investmentData: Array<TimeframeData>,
  decompsData: Array<TimeframeData>,
  isEuro: boolean,
): Array<TimeframeData> => {
  const byUtcEpoch: Record<
    number,
    {
      investment: TimeframeData | null;
      decomps: TimeframeData | null;
    }
  > = {};

  const initByUtcEpochRecordIfNecessary = (utcEpoch: number) => {
    if (!byUtcEpoch[utcEpoch]) {
      byUtcEpoch[utcEpoch] = {
        investment: null,
        decomps: null,
      };
    }
  };

  investmentData.forEach((datapoint) => {
    const utcEpoch = datapoint.utcEpoch;
    initByUtcEpochRecordIfNecessary(utcEpoch);
    byUtcEpoch[utcEpoch].investment = datapoint;
  });

  decompsData.forEach((datapoint) => {
    const utcEpoch = datapoint.utcEpoch;
    initByUtcEpochRecordIfNecessary(utcEpoch);
    byUtcEpoch[utcEpoch].decomps = datapoint;
  });

  const timeframeData: Array<TimeframeData> = [];

  Object.entries(byUtcEpoch).forEach(([utcEpoch, { investment, decomps }]) => {
    if (!investment || !decomps) {
      return;
    }

    const value = calculateRoi({
      mediaIncrementalSales: decomps.value,
      investment: investment.value,
      isEuro,
    });

    const d: TimeframeData = {
      byCampaignId: calculateRoiFromRecords(
        investment.byCampaignId,
        decomps.byCampaignId,
        isEuro,
      ),
      byMedia: calculateRoiFromRecords(
        investment.byMedia,
        decomps.byMedia,
        isEuro,
      ),
      byCampaignGroup: calculateRoiFromRecords(
        investment.byCampaignGroup,
        decomps.byCampaignGroup,
        isEuro,
      ),
      utcEpoch: Number(utcEpoch),
      value,
    };

    timeframeData.push(d);
  });

  return timeframeData.sort(sortEpoch);
};

export const aggregateRoi = (
  investments: MMDataState,
  decomps: MMDataState,
  isEuro: boolean,
): MMDataState => {
  const isLoading = investments.isLoading || decomps.isLoading;
  const isError = investments.isError || decomps.isError;

  const dataState: MMDataState = {
    week: combineTimeframeData(investments.week, decomps.week, isEuro),
    month: combineTimeframeData(investments.month, decomps.month, isEuro),
    quarter: combineTimeframeData(investments.quarter, decomps.quarter, isEuro),
    biannual: combineTimeframeData(
      investments.biannual,
      decomps.biannual,
      isEuro,
    ),
    year: combineTimeframeData(investments.year, decomps.year, isEuro),
    total: calculateRoi({
      mediaIncrementalSales: decomps.total,
      investment: investments.total,
      isEuro,
    }),
    totalByCampaignId: calculateRoiFromRecords(
      investments.totalByCampaignId,
      decomps.totalByCampaignId,
      isEuro,
    ),
    totalByMedia: calculateRoiFromRecords(
      investments.totalByMedia,
      decomps.totalByMedia,
      isEuro,
    ),
    totalByCampaignGroup: calculateRoiFromRecords(
      investments.totalByCampaignGroup,
      decomps.totalByCampaignGroup,
      isEuro,
    ),
    isLoading,
    isError,
  };

  return dataState;
};
